import { useState, useEffect, useCallback } from "react";
import { Tooltip, Modal, ModalContent, ModalHeader, ModalBody, ModalFooter, Checkbox } from "@heroui/react";
import { Button } from "components/ui/button";
import { Copy } from "@phosphor-icons/react";
import { FireSafetyInfo } from "../hooks/useFireSafetyData";

// Status color functions
const getStatusColor = (status: string) => {
  switch (status) {
    case 'Aanwezig':
      return 'bg-moss-primary';
    case 'Gedeeltelijk aanwezig':
      return 'bg-yellow-500';
    case 'Niet aanwezig':
    case 'Verplicht, en niet aanwezig':
      return 'bg-brick-primary';
    case 'Niet nodig':
    case 'Niet verplicht':
    case 'Geen brandhaspel verplicht':
    case 'Geen pictogrammen verplicht':
    case 'Geen brandtrap verplicht':
    case 'Geen lift aanwezig':
      return 'bg-transparent border-2 border-green-500';
    case 'Niet nagekeken':
      return 'bg-transparent border-2 border-black';
    default:
      return 'bg-neutral-secondary-mid';
  }
};

const getNextInspectionStatusColor = (dateString: string | null) => {
  if (!dateString) return "bg-transparent border-2 border-black";

  try {
    const nextDate = new Date(dateString);
    const today = new Date();
    const diffTime = nextDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays < 0) {
      return "bg-brick-primary";
    } else if (diffDays <= 90) {
      return "bg-orange-500";
    } else {
      return "bg-status-good";
    }
  } catch {
    return "bg-transparent border-2 border-black";
  }
};

const getNextInspectionTooltip = (dateString: string | null) => {
  if (!dateString) return "Niet ingepland";

  try {
    const nextDate = new Date(dateString);
    const today = new Date();
    const diffTime = nextDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays < 0) return "Vervallen";
    if (diffDays <= 90) return "Vervalt bijna";
    return "Nog geldig";
  } catch {
    return "Niet ingepland";
  }
};

// Format location ID to remove "Building" or "Unit" prefix
const formatLocationId = (location: string) => {
  return location.replace("Building ", "").replace("Unit ", "");
};

// Get base ID for copy grouping
const getBaseId = (location: string) => {
  const formattedLocation = formatLocationId(location);
  const segments = formattedLocation.split('-');
  if (segments.length < 2) return formattedLocation;

  let baseSegments = [];
  let foundInitialNumber = false;
  let inStringSequence = false;

  for (let i = 0; i < segments.length; i++) {
    const segment = segments[i];
    const isNumber = /^\d+$/.test(segment);

    if (!foundInitialNumber && isNumber) {
      foundInitialNumber = true;
      baseSegments.push(segment);
    } else if (foundInitialNumber && !isNumber) {
      inStringSequence = true;
      baseSegments.push(segment);
    } else if (foundInitialNumber && inStringSequence && isNumber) {
      baseSegments.push(segment);
      break;
    } else if (foundInitialNumber && !inStringSequence && isNumber) {
      baseSegments.push(segment);
    }
  }

  return baseSegments.join('-');
};

// Editable cell component
interface EditableCellProps {
  value: string | null;
  onChange: (value: string) => void;
  options?: string[];
  isDate?: boolean;
  showCopyButton?: boolean;
  onCopy?: () => void;
  fieldLabel?: string;
}

const EditableCell = ({
  value,
  onChange,
  options,
  isDate = false,
  showCopyButton = false,
  onCopy,
  fieldLabel
}: EditableCellProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value || "");

  useEffect(() => {
    setEditValue(value || "");
  }, [value]);

  const handleSave = () => {
    onChange(editValue);
    setIsEditing(false);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSave();
    if (e.key === 'Escape') {
      setEditValue(value || "");
      setIsEditing(false);
    }
  };

  if (isEditing) {
    if (options) {
      return (
        <select
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onBlur={handleSave}
          onKeyDown={handleKeyPress}
          className="w-full p-1 border border-neutral-primary rounded-sm text-sm bg-white"
          autoFocus
        >
          {options.map((option) => (
            <option key={option} value={option}>{option}</option>
          ))}
        </select>
      );
    } else if (isDate) {
      return (
        <input
          type="date"
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onBlur={handleSave}
          onKeyDown={handleKeyPress}
          className="w-full p-1 border border-neutral-primary rounded-sm text-sm bg-white"
          autoFocus
        />
      );
    } else {
      return (
        <input
          type="text"
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onBlur={handleSave}
          onKeyDown={handleKeyPress}
          className="w-full p-1 border border-neutral-primary rounded-sm text-sm bg-white"
          autoFocus
        />
      );
    }
  }

  const isEmpty = !value || value.trim() === "";

  return (
    <div className="flex items-center w-full group relative">
      <div
        className="flex-1 cursor-pointer p-2 rounded-sm hover:bg-secondary/50 w-full"
        onClick={() => setIsEditing(true)}
      >
        {isEmpty ? (
          <div className="flex items-center justify-center">
            <span className="text-neutral-secondary-mid text-sm italic">Klik om toe te voegen</span>
          </div>
        ) : options ? (
          <div className="flex items-center justify-center">
            <Tooltip content={value} placement="top">
              <span className={`inline-block h-4 w-4 rounded-full cursor-pointer ${getStatusColor(value)}`} />
            </Tooltip>
          </div>
        ) : isDate ? (
          <div className="flex items-center justify-center">
            {fieldLabel === 'Volgende keuring' ? (
              value ? (
                <div className="flex items-center gap-2">
                  <Tooltip content={getNextInspectionTooltip(value)} placement="top">
                    <span className={`inline-block h-4 w-4 rounded-full cursor-pointer ${getNextInspectionStatusColor(value)}`} />
                  </Tooltip>
                  <span className="text-sm text-neutral-primary">
                    {new Date(value).toLocaleDateString('nl-NL')}
                  </span>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <Tooltip content="Niet ingepland" placement="top">
                    <span className="inline-block h-4 w-4 rounded-full bg-transparent border-2 border-black cursor-pointer" />
                  </Tooltip>
                  <span className="text-neutral-secondary-mid text-sm italic">Niet ingepland</span>
                </div>
              )
            ) : (
              <span className="text-sm text-neutral-primary">
                {value ? new Date(value).toLocaleDateString('nl-NL') : ''}
              </span>
            )}
          </div>
        ) : (
          <div className="flex items-center justify-center">
            <span className="text-sm text-neutral-primary break-words text-center">{value}</span>
          </div>
        )}
      </div>
      {showCopyButton && onCopy && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onCopy();
          }}
          className="absolute right-1 top-1/2 transform -translate-y-1/2 opacity-0 group-hover:opacity-100 p-1 text-neutral-secondary-mid hover:text-neutral-primary transition-opacity bg-white rounded-sm shadow-sm"
          title={`Kopieer ${fieldLabel} naar alle rijen`}
        >
          <Copy size={14} />
        </button>
      )}
    </div>
  );
};

// Not applicable cell
const NotApplicableCell = () => (
  <div className="flex items-center justify-center p-2">
    <Tooltip content="Niet verplicht" placement="top">
      <span className="inline-block h-4 w-4 rounded-full bg-transparent border-2 border-green-500" />
    </Tooltip>
  </div>
);

interface FireSafetyTableProps {
  data: FireSafetyInfo[];
  sortColumn: string;
  sortDirection: "asc" | "desc";
  highlightedColumn: string | null;
  onSort: (column: string) => void;
  onUpdateField: (id: number, type: string, field: string, value: string | null) => void;
  onCopyField: (sourceInfo: FireSafetyInfo, field: string, targetItems: { id: number; type: string }[]) => Promise<boolean>;
  getStatusOptions: (fieldName: string) => string[];
}

export const FireSafetyTable = ({
  data,
  sortColumn,
  sortDirection,
  highlightedColumn,
  onSort,
  onUpdateField,
  onCopyField,
  getStatusOptions
}: FireSafetyTableProps) => {
  // Copy modal state
  const [copyModalOpen, setCopyModalOpen] = useState(false);
  const [sourceInfo, setSourceInfo] = useState<FireSafetyInfo | null>(null);
  const [sourceField, setSourceField] = useState<string>('');
  const [sourceFieldLabel, setSourceFieldLabel] = useState<string>('');
  const [selectedTargets, setSelectedTargets] = useState<{ id: number; type: string }[]>([]);
  const [isCopying, setIsCopying] = useState(false);

  // Handle copy field
  const handleCopyField = useCallback((info: FireSafetyInfo, field: string, fieldLabel: string) => {
    setSourceInfo(info);
    setSourceField(field);
    setSourceFieldLabel(fieldLabel);

    // Find compatible targets with same base ID
    const sourceBaseId = getBaseId(info.location);
    const buildingOnlyFields = [
      'emergencyLightingOnEveryFloorStatus',
      'fireExtinguisherOnEveryFloorStatus',
      'fireExtinguisherInElevatorRoomStatus',
      'fireHoseReelStatus',
      'fireEvacuationStatus',
      'fireStairStatus'
    ];

    const compatibleTargets = data.filter(item => {
      if (item.id === info.id && item.type === info.type) return false;
      const targetBaseId = getBaseId(item.location);
      if (sourceBaseId !== targetBaseId) return false;
      if (buildingOnlyFields.includes(field)) {
        return item.type === 'building';
      }
      return true;
    }).map(item => ({ id: item.id, type: item.type }));

    setSelectedTargets(compatibleTargets);
    setCopyModalOpen(true);
  }, [data]);

  // Execute copy
  const executeCopy = async () => {
    if (!sourceInfo || selectedTargets.length === 0) return;
    setIsCopying(true);
    const success = await onCopyField(sourceInfo, sourceField, selectedTargets);
    setIsCopying(false);
    if (success) {
      setCopyModalOpen(false);
    }
  };

  // Column header with sort
  const SortableHeader = ({ column, label, width }: { column: string; label: string; width: string }) => (
    <th
      className={`p-3 text-left text-sm font-semibold text-neutral-primary cursor-pointer border-r border-neutral-primary/10 ${
        highlightedColumn === column ? 'border-2 border-brick-primary bg-brick-primary/10' : ''
      }`}
      style={{ width }}
      onClick={() => onSort(column)}
    >
      <div className="flex items-center gap-1">
        {label}
        {sortColumn === column && (
          <span>{sortDirection === "asc" ? "↑" : "↓"}</span>
        )}
      </div>
    </th>
  );

  return (
    <>
      <div className="overflow-x-auto">
        <table className="w-full table-fixed border-collapse min-w-[1400px]">
          <thead>
            <tr className="bg-secondary border-b border-neutral-primary/20">
              <SortableHeader column="location" label="LOCATIE" width="120px" />
              <th className="p-3 text-left text-sm font-semibold text-neutral-primary border-r border-neutral-primary/10" style={{ width: '80px' }}>
                TYPE
              </th>
              <th
                className={`p-3 text-left text-sm font-semibold text-neutral-primary border-r border-neutral-primary/10 ${
                  highlightedColumn === 'emergencyLighting' ? 'border-2 border-brick-primary bg-brick-primary/10' : ''
                }`}
                style={{ width: '120px' }}
              >
                NOODVERLICHTING
              </th>
              <th
                className={`p-3 text-left text-sm font-semibold text-neutral-primary border-r border-neutral-primary/10 ${
                  highlightedColumn === 'fireExtinguishers' ? 'border-2 border-brick-primary bg-brick-primary/10' : ''
                }`}
                style={{ width: '120px' }}
              >
                BRANDBLUSSERS
              </th>
              <th
                className={`p-3 text-left text-sm font-semibold text-neutral-primary border-r border-neutral-primary/10 ${
                  highlightedColumn === 'fireHoseReel' ? 'border-2 border-brick-primary bg-brick-primary/10' : ''
                }`}
                style={{ width: '120px' }}
              >
                BRANDHASPEL
              </th>
              <th
                className={`p-3 text-left text-sm font-semibold text-neutral-primary border-r border-neutral-primary/10 ${
                  highlightedColumn === 'smokeDetectors' ? 'border-2 border-brick-primary bg-brick-primary/10' : ''
                }`}
                style={{ width: '100px' }}
              >
                ROOKMELDERS
              </th>
              <th
                className={`p-3 text-left text-sm font-semibold text-neutral-primary border-r border-neutral-primary/10 ${
                  highlightedColumn === 'fireProofDoors' ? 'border-2 border-brick-primary bg-brick-primary/10' : ''
                }`}
                style={{ width: '100px' }}
              >
                BRANDDEUREN
              </th>
              <th
                className={`p-3 text-left text-sm font-semibold text-neutral-primary border-r border-neutral-primary/10 ${
                  highlightedColumn === 'fireEvacuation' ? 'border-2 border-brick-primary bg-brick-primary/10' : ''
                }`}
                style={{ width: '90px' }}
              >
                EVACUATIE
              </th>
              <th
                className={`p-3 text-left text-sm font-semibold text-neutral-primary border-r border-neutral-primary/10 ${
                  highlightedColumn === 'fireStair' ? 'border-2 border-brick-primary bg-brick-primary/10' : ''
                }`}
                style={{ width: '90px' }}
              >
                BRANDTRAP
              </th>
              <SortableHeader column="fireSecurityLastInspectionDate" label="LAATSTE KEURING" width="130px" />
              <th
                className={`p-3 text-left text-sm font-semibold text-neutral-primary cursor-pointer border-r border-neutral-primary/10 ${
                  highlightedColumn === 'nextInspection' ? 'border-2 border-brick-primary bg-brick-primary/10' : ''
                }`}
                style={{ width: '130px' }}
                onClick={() => onSort("fireSecurityNextInspectionDate")}
              >
                <div className="flex items-center gap-1">
                  VOLGENDE KEURING
                  {sortColumn === "fireSecurityNextInspectionDate" && (
                    <span>{sortDirection === "asc" ? "↑" : "↓"}</span>
                  )}
                </div>
              </th>
              <th className="p-3 text-left text-sm font-semibold text-neutral-primary" style={{ width: '180px' }}>
                OPMERKINGEN
              </th>
            </tr>
          </thead>
          <tbody>
            {data.map((info) => (
              <tr key={`${info.type}-${info.id}`} className="border-b border-neutral-primary/10 hover:bg-secondary/50">
                <td className="p-3 border-r border-neutral-primary/10">{formatLocationId(info.location)}</td>
                <td className="p-3 border-r border-neutral-primary/10">
                  {info.type === 'building'
                    ? <span className="px-2 py-1 rounded-sm text-xs text-white bg-moss-primary">Gebouw</span>
                    : <span className="px-2 py-1 rounded-sm text-xs text-white bg-brick-primary">Unit</span>
                  }
                </td>
                <td className={`p-3 border-r border-neutral-primary/10 ${highlightedColumn === 'emergencyLighting' ? 'bg-brick-primary/5' : ''}`}>
                  {info.type === 'building' ? (
                    <EditableCell
                      value={info.emergencyLightingOnEveryFloorStatus}
                      onChange={(value) => onUpdateField(info.id, info.type, 'emergencyLightingOnEveryFloorStatus', value)}
                      options={getStatusOptions('emergencyLightingOnEveryFloorStatus')}
                      showCopyButton
                      onCopy={() => handleCopyField(info, 'emergencyLightingOnEveryFloorStatus', 'Noodverlichting')}
                      fieldLabel="Noodverlichting"
                    />
                  ) : <NotApplicableCell />}
                </td>
                <td className={`p-3 border-r border-neutral-primary/10 ${highlightedColumn === 'fireExtinguishers' ? 'bg-brick-primary/5' : ''}`}>
                  {info.type === 'building' ? (
                    <EditableCell
                      value={info.fireExtinguisherOnEveryFloorStatus}
                      onChange={(value) => onUpdateField(info.id, info.type, 'fireExtinguisherOnEveryFloorStatus', value)}
                      options={getStatusOptions('fireExtinguisherOnEveryFloorStatus')}
                      showCopyButton
                      onCopy={() => handleCopyField(info, 'fireExtinguisherOnEveryFloorStatus', 'Brandblussers')}
                      fieldLabel="Brandblussers"
                    />
                  ) : <NotApplicableCell />}
                </td>
                <td className={`p-3 border-r border-neutral-primary/10 ${highlightedColumn === 'fireHoseReel' ? 'bg-brick-primary/5' : ''}`}>
                  {info.type === 'building' ? (
                    <EditableCell
                      value={info.fireHoseReelStatus}
                      onChange={(value) => onUpdateField(info.id, info.type, 'fireHoseReelStatus', value)}
                      options={getStatusOptions('fireHoseReelStatus')}
                      showCopyButton
                      onCopy={() => handleCopyField(info, 'fireHoseReelStatus', 'Brandhaspel')}
                      fieldLabel="Brandhaspel"
                    />
                  ) : <NotApplicableCell />}
                </td>
                <td className={`p-3 border-r border-neutral-primary/10 ${highlightedColumn === 'smokeDetectors' ? 'bg-brick-primary/5' : ''}`}>
                  <EditableCell
                    value={info.smokeDetectorsOnEveryFloorStatus}
                    onChange={(value) => onUpdateField(info.id, info.type, 'smokeDetectorsOnEveryFloorStatus', value)}
                    options={getStatusOptions('smokeDetectorsOnEveryFloorStatus')}
                    showCopyButton
                    onCopy={() => handleCopyField(info, 'smokeDetectorsOnEveryFloorStatus', 'Rookmelders')}
                    fieldLabel="Rookmelders"
                  />
                </td>
                <td className={`p-3 border-r border-neutral-primary/10 ${highlightedColumn === 'fireProofDoors' ? 'bg-brick-primary/5' : ''}`}>
                  <EditableCell
                    value={info.fireProofDoors}
                    onChange={(value) => onUpdateField(info.id, info.type, 'fireProofDoors', value)}
                    options={getStatusOptions('fireProofDoors')}
                    showCopyButton
                    onCopy={() => handleCopyField(info, 'fireProofDoors', 'Branddeuren')}
                    fieldLabel="Branddeuren"
                  />
                </td>
                <td className={`p-3 border-r border-neutral-primary/10 ${highlightedColumn === 'fireEvacuation' ? 'bg-brick-primary/5' : ''}`}>
                  {info.type === 'building' ? (
                    <EditableCell
                      value={info.fireEvacuationStatus}
                      onChange={(value) => onUpdateField(info.id, info.type, 'fireEvacuationStatus', value)}
                      options={getStatusOptions('fireEvacuationStatus')}
                      showCopyButton
                      onCopy={() => handleCopyField(info, 'fireEvacuationStatus', 'Evacuatie')}
                      fieldLabel="Evacuatie"
                    />
                  ) : <NotApplicableCell />}
                </td>
                <td className={`p-3 border-r border-neutral-primary/10 ${highlightedColumn === 'fireStair' ? 'bg-brick-primary/5' : ''}`}>
                  {info.type === 'building' ? (
                    <EditableCell
                      value={info.fireStairStatus}
                      onChange={(value) => onUpdateField(info.id, info.type, 'fireStairStatus', value)}
                      options={getStatusOptions('fireStairStatus')}
                      showCopyButton
                      onCopy={() => handleCopyField(info, 'fireStairStatus', 'Brandtrap')}
                      fieldLabel="Brandtrap"
                    />
                  ) : <NotApplicableCell />}
                </td>
                <td className="p-3 border-r border-neutral-primary/10">
                  <EditableCell
                    value={info.fireSecurityLastInspectionDate}
                    onChange={(value) => onUpdateField(info.id, info.type, 'fireSecurityLastInspectionDate', value)}
                    isDate
                    showCopyButton
                    onCopy={() => handleCopyField(info, 'fireSecurityLastInspectionDate', 'Laatste keuring')}
                    fieldLabel="Laatste keuring"
                  />
                </td>
                <td className={`p-3 border-r border-neutral-primary/10 ${highlightedColumn === 'nextInspection' ? 'bg-brick-primary/5' : ''}`}>
                  <EditableCell
                    value={info.fireSecurityNextInspectionDate}
                    onChange={(value) => onUpdateField(info.id, info.type, 'fireSecurityNextInspectionDate', value)}
                    isDate
                    showCopyButton
                    onCopy={() => handleCopyField(info, 'fireSecurityNextInspectionDate', 'Volgende keuring')}
                    fieldLabel="Volgende keuring"
                  />
                </td>
                <td className="p-3">
                  <EditableCell
                    value={info.remarks || ""}
                    onChange={(value) => onUpdateField(info.id, info.type, 'remarks', value)}
                    showCopyButton
                    onCopy={() => handleCopyField(info, 'remarks', 'Opmerkingen')}
                    fieldLabel="Opmerkingen"
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Copy Modal */}
      <Modal
        isOpen={copyModalOpen}
        onOpenChange={setCopyModalOpen}
        size="2xl"
        scrollBehavior="inside"
      >
        <ModalContent>
          {(onClose) => (
            <>
              <ModalHeader className="flex flex-col gap-1">
                <h3 className="text-lg font-semibold text-neutral-primary">
                  {sourceFieldLabel} kopiëren
                </h3>
                {sourceInfo && (
                  <p className="text-sm text-neutral-secondary-mid">
                    Van: {sourceInfo.type === 'building' ? '🏢' : '🏠'} {formatLocationId(sourceInfo.location)}
                  </p>
                )}
              </ModalHeader>
              <ModalBody>
                <div className="space-y-3">
                  <p className="text-sm text-neutral-secondary-mid">
                    Selecteer naar welke items u deze waarde wilt kopiëren:
                  </p>
                  {selectedTargets.length === 0 ? (
                    <p className="text-sm text-neutral-secondary-mid text-center py-4">
                      Geen compatibele items gevonden om naar te kopiëren.
                    </p>
                  ) : (
                    selectedTargets.map((target) => {
                      const targetInfo = data.find(info => info.id === target.id && info.type === target.type);
                      return (
                        <div key={`${target.type}-${target.id}`} className="flex items-center p-3 border border-neutral-primary/20 rounded-lg">
                          <Checkbox
                            isSelected={true}
                            onValueChange={(checked) => {
                              if (!checked) {
                                setSelectedTargets(prev => prev.filter(t => !(t.id === target.id && t.type === target.type)));
                              }
                            }}
                            color="primary"
                          >
                            <span className="font-medium">
                              {target.type === 'building' ? '🏢' : '🏠'} {targetInfo ? formatLocationId(targetInfo.location) : `${target.type} ${target.id}`}
                            </span>
                          </Checkbox>
                        </div>
                      );
                    })
                  )}
                </div>
              </ModalBody>
              <ModalFooter>
                <Button
                  variant="ghost"
                  onClick={onClose}
                  disabled={isCopying}
                >
                  Annuleren
                </Button>
                <Button
                  onClick={executeCopy}
                  disabled={selectedTargets.length === 0 || isCopying}
                  className="bg-moss-primary text-white hover:bg-moss-primary/90"
                >
                  {isCopying ? 'Kopiëren...' : `Kopiëren naar ${selectedTargets.length} item(s)`}
                </Button>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>
    </>
  );
};
